/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import {Controller, useForm} from 'react-hook-form';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete, {AutocompleteProps} from '@mui/material/Autocomplete';
import {countries} from 'src/assets/data';
import Iconify from '../iconify/iconify';
import {CircularProgress} from '@mui/material';

// ----------------------------------------------------------------------

interface Props<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  name: string;
  label?: string;
  placeholder?: string;
  type?: 'country' | string;
  helperText?: React.ReactNode;
  defaultLegacyAppValue?: any;
  InputProps?: any;
  errors?:any
}

export default function RHFAutocomplete<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
>({
  InputProps,
  name,
  label,
  type,
  helperText,
  placeholder,
  fullWidth,
  onChange,
  defaultLegacyAppValue,
  errors,
  ...other
}: Omit<Props<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'>) {
  const {control, setValue} = useForm({
    mode: 'onChange'
  });
  const {multiple} = other;

  return (
    <Controller
      name={name}
      control={control}
      render={({field, fieldState: {error}}) => {
        if (type === 'country') {
          return (
            <Autocomplete
              {...field}
              id={`autocomplete-${name}`}
              autoHighlight={!multiple}
              disableCloseOnSelect={multiple}
              onChange={(event, newValue) => {
                setValue(name, newValue, {shouldValidate: true});
                if (onChange) {
                  onChange(event, newValue, 'selectOption');
                }
              }}
              renderOption={(props, option) => {
                const country = getCountry(option as string);

                if (!country.label) {
                  return null;
                }

                return (
                  <li {...props} key={country.label}>
                    <Iconify
                      key={country.label}
                      icon={`circle-flags:${country.code?.toLowerCase()}`}
                      sx={{mr: 1}}
                    />
                    {country.label} ({country.code}) +{country.phone}
                  </li>
                );
              }}
              renderInput={params => {
                const country = getCountry(params.inputProps.value as string);

                const baseField = {
                  ...params,
                  label,
                  placeholder,
                  error: !!error,
                  helperText: error ? error?.message : helperText,
                  inputProps: {
                    ...params.inputProps,
                    autoComplete: 'new-password'
                  }
                };

                if (multiple) {
                  return <TextField {...baseField} />;
                }

                return (
                  <TextField
                    {...baseField}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment
                          position="start"
                          sx={{
                            ...(!country.code && {
                              display: 'none'
                            })
                          }}>
                          <Iconify
                            icon={`circle-flags:${country.code?.toLowerCase()}`}
                            sx={{mr: -0.5, ml: 0.5}}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                );
              }}
              renderTags={(selected, getTagProps) =>
                selected.map((option, index) => {
                  const country = getCountry(option as string);

                  return (
                    <Chip
                      {...getTagProps({index})}
                      key={country.label}
                      label={country.label}
                      icon={
                        <Iconify
                          icon={`circle-flags:${country.code?.toLowerCase()}`}
                        />
                      }
                      size="small"
                      variant="soft"
                    />
                  );
                })
              }
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0000003b',
                  borderWidth: '1.8px'
                }
              }}
              {...other}
            />
          );
        }

        return (
          <Autocomplete
            {...field}
            id={`autocomplete-${name}`}
            onChange={(event, newValue) => {
              setValue(name, newValue, {shouldValidate: true});
              if (onChange) {
                onChange(event, newValue, 'selectOption');
              }
            }}
            fullWidth={fullWidth}
            renderInput={params => (
              <TextField
                {...params}
                InputProps={{
                  ...params.InputProps, // Ensure Autocomplete's InputProps are merged
                  ...InputProps // Add your custom InputProps
                }}
                label={label}
                placeholder={placeholder}
                error={!!error || errors}
                helperText={error ? error?.message : helperText}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'new-password'
                }}
              />
            )}
            {...other}
          />
        );
      }}
    />
  );
}

// ----------------------------------------------------------------------

export function getCountry(inputValue: string) {
  const option = countries.filter(country => country.label === inputValue)[0];

  return {
    ...option
  };
}
